<template>
  <!-- Question-Details-pattern dialog for a single key result: the one place
       an HR leader sees the full untruncated title, the progress trajectory,
       every owner, the complete update history, and the automation wiring.
       Opens on row click; opens in readonly mode too — visibility is the
       point, only mutating actions hide. -->
  <el-dialog
    :visible.sync="dialogVisible"
    width="720px"
    top="6vh"
    custom-class="kr-detail-dialog"
    append-to-body
    @opened="renderChart"
    @closed="destroyChart"
  >
    <div v-if="keyResult" slot="title" class="krd-header">
      <div class="qd-eyebrow">{{ eyebrowText }}</div>
      <div class="krd-title">{{ keyResult.title }}</div>
      <div class="krd-pill-row">
        <span v-if="keyResult.cycle" class="krd-cycle-pill">{{ keyResult.cycle }}</span>
        <KrStatusBadge
          :key-result="keyResult"
          :readonly="readonly"
          @change="onStatusChange"
        />
        <AutomationSourcePill
          v-if="metricSource && metricSource.toolkit_slug"
          :toolkit-slug="metricSource.toolkit_slug"
          :paused="metricSource.enabled === false"
        />
      </div>
    </div>

    <div v-if="keyResult" class="krd-body">
      <!-- 1. PROGRESS HERO -->
      <section class="krd-hero">
        <div class="krd-hero__stat-row">
          <span class="krd-hero__percent" :style="{ color: statusColor }">{{ progressPercent }}%</span>
          <span class="krd-hero__percent-label">{{ $t('performance.goals.kr_detail_of_target') }}</span>
          <span
            v-if="latestDelta"
            class="krd-hero__delta"
            :class="latestDelta.up ? 'krd-hero__delta--up' : 'krd-hero__delta--down'"
          >
            {{ $t('performance.goals.kr_detail_delta_since', { delta: latestDelta.text }) }}
          </span>
        </div>
        <div class="krd-hero__bar">
          <div
            class="krd-hero__bar-fill"
            :style="{ width: progressPercent + '%', backgroundColor: statusColor }"
          />
        </div>
        <div class="krd-hero__caption">
          {{ formatValue(keyResult.start_value) }}
          <span class="krd-hero__arrow">→</span>
          <strong class="krd-hero__current">{{ formatValue(effectiveValue) }}</strong>
          <span class="krd-hero__sep">/</span>
          {{ formatValue(keyResult.target_value) }}
        </div>
      </section>

      <!-- 2. PROGRESS OVER TIME -->
      <section class="krd-section">
        <div class="qd-eyebrow">{{ $t('performance.goals.kr_detail_chart_title') }}</div>
        <div v-if="hasChart" class="krd-chart">
          <canvas ref="chartCanvas" />
        </div>
        <div v-else class="krd-empty">
          {{ $t('performance.goals.kr_detail_chart_empty') }}
        </div>
      </section>

      <!-- 3. OWNERS -->
      <section class="krd-section">
        <div class="krd-section-head">
          <div class="qd-eyebrow">{{ $t('performance.goals.kr_detail_owners') }}</div>
          <span v-if="ownersData.length > 0" class="krd-section-hint">
            {{ isIndividual
              ? $t('performance.goals.tracking_individual_hint')
              : $t('performance.goals.tracking_collaborative_hint') }}
          </span>
        </div>

        <div v-if="ownersData.length === 0" class="krd-empty">
          {{ $t('performance.goals.kr_detail_no_owners') }}
        </div>

        <!-- Individual mode: each owner's progress against their own target -->
        <div v-else-if="isIndividual" class="krd-owner-rows">
          <div
            v-for="row in individualRows"
            :key="row.email"
            class="krd-owner-row"
            :title="row.customized ? $t('performance.goals.owner_target_customized_hint') : ''"
          >
            <div class="krd-owner-avatar" :style="avatarStyle(row)">
              <span v-if="!row.large_pic">{{ getInitials(row.fullname) }}</span>
            </div>
            <span class="krd-owner-name">{{ row.fullname }}</span>
            <div class="krd-owner-bar">
              <div
                class="krd-owner-bar-fill"
                :style="{ width: row.percent + '%', backgroundColor: statusColor }"
              />
            </div>
            <span class="krd-owner-value">{{ formatValue(row.value) }} / {{ formatValue(row.target) }}</span>
            <span v-if="row.customized" class="kr-custom-tag">{{ $t('performance.goals.owner_target_customized_tag') }}</span>
          </div>
        </div>

        <!-- Collaborative mode: everyone contributes to one number -->
        <div v-else class="krd-owner-chips">
          <div v-for="owner in ownersData" :key="owner.email" class="krd-owner-chip">
            <div class="krd-owner-avatar" :style="avatarStyle(owner)">
              <span v-if="!owner.large_pic">{{ getInitials(owner.fullname) }}</span>
            </div>
            <span class="krd-owner-chip-name">{{ owner.fullname }}</span>
          </div>
        </div>
      </section>

      <!-- 4. UPDATE HISTORY -->
      <section class="krd-section">
        <div class="krd-section-head">
          <div class="qd-eyebrow">{{ $t('performance.goals.kr_history_title') }}</div>
          <span v-if="historyCount > 0" class="krd-history-count">{{ historyCount }}</span>
        </div>
        <div class="krd-history">
          <KrTimelineList :key-result="keyResult" :cap="0" :list-max-height="300" />
        </div>
      </section>

      <!-- 5. AUTOMATION (only when a metric source is wired) -->
      <section v-if="metricSource && metricSource.toolkit_slug" class="krd-section">
        <div class="qd-eyebrow">{{ $t('performance.goals.kr_detail_automation') }}</div>
        <div class="krd-automation">
          <AutomationToolkitMark
            :slug="metricSource.toolkit_slug"
            :name="toolkitName"
            :logo-url="toolkitLogoUrl"
            size="lg"
          />
          <div class="krd-automation__text">
            <span class="krd-automation__name">{{ toolkitName }}</span>
            <span class="krd-automation__meta">
              {{ automationStatusText }}
              <template v-if="cadenceText"> · {{ cadenceText }}</template>
            </span>
          </div>
          <div v-if="!readonly" class="krd-automation__actions">
            <button
              v-if="!automationError"
              type="button"
              class="krd-automation__edit"
              :disabled="automationBusy"
              @click="openAutomation"
            >
              {{ $t('performance.goals.kr_detail_edit_automation') }}
            </button>
            <button
              type="button"
              class="krd-automation__edit"
              :disabled="automationBusy"
              @click="toggleAutomation"
            >
              {{ metricSource.enabled === false
                ? $t('performance.goals.kr_detail_resume_automation')
                : $t('performance.goals.kr_detail_pause_automation') }}
            </button>
            <button
              type="button"
              class="krd-automation__edit krd-automation__delete"
              :disabled="automationBusy"
              @click="deleteAutomation"
            >
              {{ $t('performance.goals.kr_detail_delete_automation') }}
            </button>
          </div>
        </div>
        <div v-if="automationError" class="krd-automation-error">
          <i class="el-icon-warning-outline" />
          <span class="krd-automation-error__msg">{{ automationError.message }}</span>
          <el-button v-if="!readonly" size="mini" type="primary" @click="openAutomation">
            {{ $t('performance.goals.kr_detail_reconnect') }}
          </el-button>
        </div>
      </section>
    </div>

    <div v-if="keyResult && !readonly" slot="footer" class="krd-footer">
      <el-button size="small" @click="onEditKeyResult">
        {{ $t('performance.goals.kr_detail_edit_kr') }}
      </el-button>
      <div class="krd-footer__spacer" />
      <template v-if="!updating">
        <el-button type="primary" size="small" @click="startUpdate">
          {{ $t('performance.goals.kr_detail_update_progress') }}
        </el-button>
      </template>
      <template v-else>
        <el-input-number
          v-model="updateValue"
          size="small"
          :min="0"
          controls-position="right"
          class="krd-update-input"
        />
        <el-input
          v-model="updateNote"
          size="small"
          :placeholder="$t('performance.goals.kr_note_placeholder')"
          class="krd-update-note"
        />
        <el-button size="small" @click="cancelUpdate">{{ $t('global.Cancel') }}</el-button>
        <el-button type="primary" size="small" @click="saveUpdate">
          {{ $t('performance.goals.kr_detail_save_update') }}
        </el-button>
      </template>
    </div>
  </el-dialog>
</template>

<script>
import Chart from 'chart.js/auto';
import { KR_STATUS_COLORS } from '@/constants/krStatus';
import { computeOwnerTargetMap } from '@/store/goals';
import {
  buildKrChartSeries,
  buildKrTimeline,
  buildOwnerProgressRows,
  calculateKrProgress,
  formatKrNumber,
  formatKrValue,
  formatRelativeKrTime,
  getEffectiveKrValue,
  isAutomationActor,
  krCadenceParts,
  toolkitDisplayName,
} from '@/store/utils/krUpdateRules';
import { simpleIconUrl } from '@/services/krAutomationDemoBackend';
import AutomationSourcePill from './AutomationSourcePill.vue';
import AutomationToolkitMark from './AutomationToolkitMark.vue';
import KrStatusBadge from './KrStatusBadge.vue';
import KrTimelineList from './KrTimelineList.vue';

// Fallback line colors for per-owner chart series when a member has no
// house_color — drawn from the brand palette.
const FALLBACK_SERIES_COLORS = ['#6C30CC', '#3AC47D', '#F7B924', '#AECFFE', '#CC3030', '#00b383'];

export default {
  name: 'KeyResultDetailDialog',
  components: { AutomationSourcePill, AutomationToolkitMark, KrStatusBadge, KrTimelineList },
  props: {
    visible: { type: Boolean, default: false },
    keyResult: { type: Object, default: null },
    goal: { type: Object, default: null },
    teamId: { type: String, default: '' },
    readonly: { type: Boolean, default: false },
    automationBusy: { type: Boolean, default: false },
  },
  data() {
    return {
      updating: false,
      updateValue: 0,
      updateNote: '',
      chartInstance: null,
    };
  },
  computed: {
    dialogVisible: {
      get() { return this.visible; },
      set(v) { if (!v) this.$emit('close'); },
    },
    eyebrowText() {
      const goalLabel = this.goal
        ? (this.goal.short_name || this.goal.quick_preview || '')
        : '';
      const base = this.$t('performance.goals.kr_detail_eyebrow');
      return goalLabel ? `${base} · ${goalLabel}` : base;
    },
    statusColor() {
      return KR_STATUS_COLORS[this.keyResult && this.keyResult.status] || '#909399';
    },
    isIndividual() {
      return !!this.keyResult && this.keyResult.mode === 'individual';
    },
    effectiveValue() {
      return getEffectiveKrValue(this.keyResult || {});
    },
    progressPercent() {
      return Math.round(calculateKrProgress(this.keyResult || {}));
    },
    timeline() {
      return buildKrTimeline(
        this.keyResult ? this.keyResult.progress_history : [],
        this.keyResult || undefined,
      );
    },
    historyCount() {
      return this.timeline.length;
    },
    latestDelta() {
      const newest = this.timeline[0];
      if (!newest || newest.delta === null || newest.delta === 0) return null;
      const sign = newest.delta > 0 ? '+' : '-';
      return {
        up: newest.delta > 0,
        text: sign + formatKrValue(Math.abs(newest.delta), this.keyResult.unit),
      };
    },
    metricSource() {
      return (this.keyResult && this.keyResult.metric_source) || null;
    },
    automationError() {
      return (this.metricSource && this.metricSource.last_error) || null;
    },
    toolkitName() {
      return toolkitDisplayName(this.metricSource ? this.metricSource.toolkit_slug : '');
    },
    toolkitLogoUrl() {
      return this.metricSource ? simpleIconUrl(this.metricSource.toolkit_slug) : '';
    },
    automationStatusText() {
      const source = this.metricSource;
      if (!source) return '';
      if (source.enabled === false) return this.$t('performance.goals.kr_detail_automation_paused');
      if (source.last_synced_at) {
        return this.$t('performance.goals.kr_detail_last_synced', {
          time: formatRelativeKrTime(source.last_synced_at),
        });
      }
      if (source.last_checked_at) {
        return this.$t('performance.goals.kr_detail_last_checked', {
          time: formatRelativeKrTime(source.last_checked_at),
        });
      }
      return this.$t('performance.goals.kr_detail_awaiting_sync');
    },
    cadenceText() {
      const parts = krCadenceParts(this.metricSource ? this.metricSource.cadence : null);
      if (!parts) return '';
      if (parts.kind === 'daily') {
        return this.$t('performance.goals.kr_detail_cadence_daily', { time: parts.time });
      }
      if (parts.kind === 'monthly') {
        return this.$t('performance.goals.kr_detail_cadence_monthly', {
          dayOfMonth: parts.dayOfMonth, time: parts.time,
        });
      }
      return this.$t('performance.goals.kr_detail_cadence_weekly', { day: parts.day, time: parts.time });
    },
    ownersData() {
      const emails = (this.keyResult && this.keyResult.owner_emails) || [];
      return emails.map((email) => {
        const data = this.$store.getters['goals/resolveOwner'](email);
        return {
          email,
          fullname: data && data.fullname ? data.fullname : email,
          house_color: (data && data.house_color) || '#ddd',
          large_pic: (data && data.large_pic) || '',
        };
      });
    },
    individualRows() {
      if (!this.isIndividual || !this.keyResult) return [];
      const kr = this.keyResult;
      const targetMap = computeOwnerTargetMap(kr.owner_emails, kr.target_value, kr.owner_targets || {});
      return buildOwnerProgressRows(kr, targetMap).map((row) => {
        const data = this.$store.getters['goals/resolveOwner'](row.email);
        return {
          ...row,
          fullname: data && data.fullname ? data.fullname : row.email,
          house_color: (data && data.house_color) || '#ddd',
          large_pic: (data && data.large_pic) || '',
        };
      });
    },
    // Individual-mode KRs with several owners chart one slice line per owner
    // (from entry.owner_value — entry.value is the team rollup). The builder
    // falls back to a single rollup line when legacy entries carry no slices,
    // so trust chartData.grouped, not this request flag, when rendering.
    chartGrouped() {
      return this.isIndividual && ((this.keyResult.owner_emails || []).length > 1);
    },
    chartData() {
      if (!this.keyResult) return { grouped: false, series: [] };
      return buildKrChartSeries(this.keyResult.progress_history, { groupByActor: this.chartGrouped });
    },
    chartSeries() {
      return this.chartData.series;
    },
    hasChart() {
      const total = this.chartSeries.reduce((sum, s) => sum + s.points.length, 0);
      return total >= 2;
    },
    // One string that changes whenever the chart needs a repaint.
    chartSignature() {
      return JSON.stringify({
        grouped: this.chartData.grouped,
        series: this.chartSeries,
        target: this.keyResult ? this.keyResult.target_value : null,
        color: this.statusColor,
      });
    },
  },
  watch: {
    visible(open) {
      if (!open) {
        this.updating = false;
        this.updateNote = '';
      }
    },
    chartSignature() {
      if (this.visible) {
        this.$nextTick(() => this.renderChart());
      }
    },
  },
  beforeDestroy() {
    this.destroyChart();
  },
  methods: {
    formatValue(value) {
      return formatKrValue(value, this.keyResult ? this.keyResult.unit : '');
    },
    getInitials(name) {
      if (!name) return '?';
      const parts = String(name).trim().split(/\s+/);
      if (parts.length >= 2) return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
      return (parts[0][0] || '?').toUpperCase();
    },
    avatarStyle(person) {
      return {
        backgroundColor: person.house_color || '#ddd',
        backgroundImage: person.large_pic ? 'url(' + person.large_pic + ')' : 'none',
      };
    },
    onStatusChange(status) {
      this.$emit('update-status', { krId: this.keyResult.kr_id, status });
    },
    onEditKeyResult() {
      this.$emit('edit-key-result', { krId: this.keyResult.kr_id });
    },
    openAutomation() {
      this.$emit('open-automation', { krId: this.keyResult.kr_id });
    },
    toggleAutomation() {
      this.$emit('toggle-automation', {
        enabled: this.metricSource ? this.metricSource.enabled === false : false,
      });
    },
    deleteAutomation() {
      this.$emit('delete-automation');
    },
    startUpdate() {
      this.updateValue = this.keyResult.current_value;
      this.updateNote = '';
      this.updating = true;
    },
    cancelUpdate() {
      this.updating = false;
      this.updateNote = '';
    },
    saveUpdate() {
      // Same event shape as KeyResultRow — goals.vue routes both through
      // goals/updateKeyResultProgress with updatedBy.
      this.$emit('update-progress', {
        krId: this.keyResult.kr_id,
        newValue: this.updateValue,
        note: this.updateNote,
      });
      this.updating = false;
      this.updateNote = '';
    },
    seriesLabel(actor) {
      if (!actor) return this.$t('performance.goals.kr_detail_chart_title');
      if (isAutomationActor(actor)) {
        return this.metricSource && this.metricSource.toolkit_slug
          ? toolkitDisplayName(this.metricSource.toolkit_slug)
          : this.$t('performance.goals.kr_history_automation_actor');
      }
      const data = this.$store.getters['goals/resolveOwner'](actor);
      return data && data.fullname ? data.fullname : actor.split('@')[0];
    },
    seriesColor(actor, index) {
      if (!actor || isAutomationActor(actor)) return '#6C30CC';
      const data = this.$store.getters['goals/resolveOwner'](actor);
      if (data && data.house_color) return data.house_color;
      return FALLBACK_SERIES_COLORS[index % FALLBACK_SERIES_COLORS.length];
    },
    destroyChart() {
      if (this.chartInstance) {
        this.chartInstance.destroy();
        this.chartInstance = null;
      }
    },
    renderChart() {
      this.destroyChart();
      if (!this.hasChart || !this.keyResult) return;
      const canvas = this.$refs.chartCanvas;
      if (!canvas) return;

      const kr = this.keyResult;
      const unit = kr.unit || '';
      const grouped = this.chartData.grouped;
      const series = this.chartSeries;

      const allPoints = series.reduce((acc, s) => acc.concat(s.points), []);
      const minT = Math.min(...allPoints.map((p) => p.t));
      const maxT = Math.max(...allPoints.map((p) => p.t));
      const values = allPoints.map((p) => p.value);
      const target = Number(kr.target_value) || 0;
      const start = Number(kr.start_value) || 0;
      // Grouped lines plot per-owner slices, so the y-scale should top out at
      // the largest owner target, not the team target the rollup line uses.
      const ownerTargets = grouped
        ? Object.values(computeOwnerTargetMap(kr.owner_emails, kr.target_value, kr.owner_targets || {}))
        : [];
      const yMax = grouped
        ? Math.max(...values, ...ownerTargets.map((n) => Number(n) || 0))
        : Math.max(target, ...values);

      const datasets = series.map((s, idx) => {
        const color = grouped ? this.seriesColor(s.actor, idx) : this.statusColor;
        return {
          label: grouped ? this.seriesLabel(s.actor) : this.seriesLabel(''),
          data: s.points.map((p) => ({ x: p.t, y: p.value })),
          borderColor: color,
          backgroundColor: grouped ? color : color + '1A',
          fill: !grouped,
          tension: 0.3,
          borderWidth: 2,
          pointRadius: 3,
          pointHoverRadius: 4,
          pointBackgroundColor: color,
        };
      });

      // Horizontal target reference — only meaningful when the plotted values
      // share the team target (collaborative / single-owner KRs).
      if (!grouped) {
        datasets.push({
          label: this.$t('performance.goals.kr_detail_target_label'),
          data: [{ x: minT, y: target }, { x: maxT, y: target }],
          borderColor: '#8a8a8a',
          borderDash: [6, 4],
          borderWidth: 1.5,
          pointRadius: 0,
          pointHoverRadius: 0,
          fill: false,
        });
      }

      const dateLabel = (ms, withYear) => new Date(ms).toLocaleDateString(
        undefined,
        withYear ? { year: 'numeric', month: 'short', day: 'numeric' } : { month: 'short', day: 'numeric' },
      );

      this.chartInstance = new Chart(canvas, {
        type: 'line',
        data: { datasets },
        options: {
          responsive: true,
          maintainAspectRatio: false,
          interaction: { mode: 'nearest', intersect: false },
          plugins: {
            legend: {
              display: grouped,
              position: 'bottom',
              labels: {
                usePointStyle: true,
                boxWidth: 6,
                boxHeight: 6,
                font: { family: 'Lato', size: 11 },
                color: '#626262',
              },
            },
            tooltip: {
              displayColors: false,
              backgroundColor: '#1a1a1a',
              titleFont: { family: 'Lato', size: 11 },
              bodyFont: { family: 'Lato', size: 12, weight: '700' },
              callbacks: {
                title: (items) => (items.length ? dateLabel(items[0].parsed.x, true) : ''),
                label: (item) => {
                  const val = formatKrValue(item.parsed.y, unit);
                  return grouped ? `${item.dataset.label}: ${val}` : val;
                },
              },
            },
          },
          scales: {
            x: {
              type: 'linear',
              min: minT,
              max: maxT,
              grid: { display: false },
              ticks: {
                maxTicksLimit: 6,
                font: { family: 'Lato', size: 10 },
                color: '#9a9a9a',
                callback: (value) => dateLabel(value, false),
              },
            },
            y: {
              suggestedMin: Math.min(start, ...values),
              suggestedMax: yMax,
              grid: { color: '#eef0f5', drawBorder: false },
              ticks: {
                maxTicksLimit: 5,
                font: { family: 'Lato', size: 10 },
                color: '#9a9a9a',
                callback: (value) => formatKrNumber(value),
              },
            },
          },
        },
      });
    },
  },
};
</script>

<style>
/* Global selector for el-dialog custom-class (Element UI teleports the dialog
   out of component scope). Paddings mirror the automation dialog. */
.kr-detail-dialog .el-dialog__header {
  padding: 24px 32px 16px 32px;
}
.kr-detail-dialog .el-dialog__body {
  padding: 4px 32px 20px 32px;
}
.kr-detail-dialog .el-dialog__footer {
  padding: 14px 32px;
  background: #fafbfd;
  border-top: 1px solid #eef0f5;
}
</style>

<style scoped>
.krd-header {
  display: flex;
  flex-direction: column;
  gap: 8px;
  /* Keep the title clear of Element UI's absolute-positioned close button. */
  padding-right: 24px;
}

.krd-title {
  font-size: 18px;
  font-weight: 700;
  color: #1a1a1a;
  line-height: 1.35;
  word-break: break-word;
}

.krd-pill-row {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
  margin-top: 2px;
}

.krd-cycle-pill {
  display: inline-flex;
  align-items: center;
  height: 20px;
  padding: 0 10px;
  border-radius: 999px;
  background: #f5f5fa;
  color: #626262;
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  white-space: nowrap;
}

.krd-body {
  display: flex;
  flex-direction: column;
}

/* Sections divided by subtle rules — QD-style rhythm. */
.krd-section {
  padding: 18px 0;
  border-top: 1px solid #eef0f5;
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.krd-section-head {
  display: flex;
  align-items: baseline;
  gap: 8px;
}

.krd-section-hint {
  font-size: 11px;
  font-weight: 500;
  color: #9a9a9a;
}

.krd-history-count {
  font-size: 11px;
  font-weight: 700;
  color: #6c30cc;
  background: #f2ecfb;
  border-radius: 999px;
  padding: 1px 8px;
  line-height: 1.5;
}

/* ── Progress hero ─────────────────────────────────────────────── */
.krd-hero {
  padding: 6px 0 18px;
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.krd-hero__stat-row {
  display: flex;
  align-items: baseline;
  gap: 8px;
}

.krd-hero__percent {
  font-size: 22px;
  font-weight: 900;
  line-height: 1;
}

.krd-hero__percent-label {
  font-size: 10px;
  font-weight: 700;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  color: #9a9a9a;
}

.krd-hero__delta {
  margin-left: auto;
  font-size: 12px;
  font-weight: 700;
  white-space: nowrap;
}

.krd-hero__delta--up {
  color: #1f8b5a;
}

.krd-hero__delta--down {
  color: #b22a2a;
}

.krd-hero__bar {
  height: 8px;
  background: #eef0f5;
  border-radius: 999px;
  overflow: hidden;
}

.krd-hero__bar-fill {
  height: 100%;
  border-radius: 999px;
  transition: width 0.3s ease;
}

.krd-hero__caption {
  font-size: 13px;
  font-weight: 500;
  color: #626262;
}

.krd-hero__current {
  font-weight: 900;
  color: #1a1a1a;
}

.krd-hero__arrow,
.krd-hero__sep {
  color: #9a9a9a;
  margin: 0 2px;
}

/* ── Chart ─────────────────────────────────────────────────────── */
.krd-chart {
  position: relative;
  height: 200px;
}

/* Quiet empty state — dashed bubble per the design-system motif. */
.krd-empty {
  border: 1px dashed #e2e4ea;
  border-radius: 12px;
  padding: 18px;
  font-size: 12px;
  font-weight: 500;
  color: #9a9a9a;
  text-align: center;
}

/* ── Owners ────────────────────────────────────────────────────── */
.krd-owner-chips {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

.krd-owner-chip {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 4px 12px 4px 4px;
  border: 1px solid #eef0f5;
  border-radius: 999px;
  background: #ffffff;
}

.krd-owner-chip-name {
  font-size: 12px;
  font-weight: 700;
  color: #4a4a4a;
  white-space: nowrap;
}

.krd-owner-rows {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.krd-owner-row {
  display: flex;
  align-items: center;
  gap: 10px;
  padding-left: 6px;
}

/* Muted uppercase tag marking an owner whose target differs from the
   default share (replaces the old purple left-border accent). */
.kr-custom-tag {
  font-size: 10px;
  font-weight: 700;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: #8a8a8a;
  flex-shrink: 0;
}

.krd-owner-avatar {
  width: 24px;
  height: 24px;
  border-radius: 50%;
  background-size: cover;
  background-position: center;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-size: 10px;
  font-weight: 700;
  color: white;
  flex-shrink: 0;
}

.krd-owner-name {
  width: 160px;
  font-size: 12px;
  font-weight: 700;
  color: #4a4a4a;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  flex-shrink: 0;
}

.krd-owner-bar {
  flex: 1;
  min-width: 0;
  height: 6px;
  background: #eef0f5;
  border-radius: 999px;
  overflow: hidden;
}

.krd-owner-bar-fill {
  height: 100%;
  border-radius: 999px;
  transition: width 0.3s ease;
}

.krd-owner-value {
  font-size: 12px;
  font-weight: 700;
  color: #626262;
  white-space: nowrap;
  flex-shrink: 0;
  min-width: 70px;
  text-align: right;
}

/* ── History ───────────────────────────────────────────────────── */
.krd-history {
  /* KrTimelineList pads its own rows; pull them flush with the section. */
  margin: 0 -10px;
}

/* ── Automation ────────────────────────────────────────────────── */
.krd-automation {
  display: flex;
  align-items: center;
  gap: 12px;
}

.krd-automation__text {
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 0;
}

.krd-automation__name {
  font-size: 13px;
  font-weight: 700;
  color: #1a1a1a;
}

.krd-automation__meta {
  font-size: 12px;
  font-weight: 500;
  color: #8a8a8a;
}

.krd-automation__edit {
  margin-left: auto;
  font-family: inherit;
  font-size: 12px;
  font-weight: 700;
  color: #6c30cc;
  background: #f2ecfb;
  border: none;
  border-radius: 8px;
  padding: 6px 12px;
  cursor: pointer;
  transition: background-color 160ms ease;
  flex-shrink: 0;
}

.krd-automation__edit:hover {
  background: #e6dafa;
}

.krd-automation__actions {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  flex-wrap: wrap;
  gap: 4px;
  margin-left: auto;
}

.krd-automation__actions .krd-automation__edit {
  margin-left: 0;
}

.krd-automation__edit:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.krd-automation__delete {
  color: #cc3030;
  background: #fff2f2;
}

.krd-automation__delete:hover:not(:disabled) {
  background: #ffe0e0;
  color: #a92222;
}

.krd-automation-error {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 12px;
  background: #fff8ec;
  border: 1px solid #f5dfa2;
  border-radius: 8px;
  font-size: 12px;
  color: #4a4a4a;
}

.krd-automation-error__msg {
  flex: 1;
}

/* ── Footer ────────────────────────────────────────────────────── */
.krd-footer {
  display: flex;
  align-items: center;
  gap: 8px;
}

.krd-footer__spacer {
  flex: 1;
}

.krd-update-input {
  width: 140px;
}

.krd-update-note {
  width: 220px;
}
</style>
